import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDownUp,
  ChevronDown,
  Loader2,
  Search,
  TrendingUp,
  Store,
  ArrowRight,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { timeAgo } from "@/lib/buyfiat";
import { makeRequest } from "@/utils/makeRequest";
import { formatEvmHumanBalance } from "@/utils";

interface Currency {
  id?: string;
  code: string;
  name: string;
  symbol?: string | null;
  icon?: string | null;
  iso?: string | null;
  iso2?: string | null;
  flag?: string | null;
  enabled: boolean;
  sort_order?: number;
  type?: string | null;
  decimals?: number;
  isFiat?: boolean;
}

interface ConverterQuote {
  from: {
    id?: string;
    symbol: string;
    code: string | null;
    iso?: string | null;
    iso2?: string | null;
    name: string;
    decimals: number;
    isFiat: boolean;
  };
  to: {
    id?: string;
    symbol: string;
    code: string | null;
    iso?: string | null;
    iso2?: string | null;
    name: string;
    decimals: number;
    isFiat: boolean;
  };
  amount: string;
  rate: string;
  destinationAmount: string;
  accounting: {
    currency: string;
    sourceAmount: string;
    destinationAmount: string;
  };
  generatedAt: string;
}

interface ConverterMarket {
  from: string;
  to: string;
  available: boolean;
  merchantCount: number;
  liquidity: string;
  liquidityCurrency: string;
  lowRate: string;
  highRate: string;
  rateCurrency: string;
  quoteCurrency: string;
  message?: string;
}

interface CurrencySelectProps {
  value: string;
  onChange: (value: string) => void;
  currencies: Currency[];
  loading?: boolean;
  label: string;
}

interface ConverterProps {
  initialFrom?: string;
  initialTo?: string;
  initialAmount?: string | number;
  compact?: boolean;
}

function getCurrencyType(currency?: Currency): string {
  if (!currency) return "";

  return (
    currency.type ||
    (["NGN", "USD", "EUR", "GBP", "KES", "GHS", "ZAR", "CAD", "AUD"].includes(
      currency.code.toUpperCase(),
    )
      ? "fiat"
      : "crypto")
  );
}

function CurrencyFlag({ currency }: { currency?: Currency }) {
  const [imageFailed, setImageFailed] = useState(false);

  const isFiat = currency?.isFiat ?? currency?.type?.toLowerCase() === "fiat";

  const countryCode = currency?.iso2?.trim().toLowerCase();

  const imageUrl = !currency
    ? undefined
    : isFiat
      ? countryCode
        ? `https://flagcdn.com/w80/${countryCode}.png`
        : undefined
      : currency.icon?.trim() || undefined;

  useEffect(() => {
    setImageFailed(false);
  }, [imageUrl]);

  if (!currency) {
    return (
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-bold text-muted-foreground">
        ?
      </div>
    );
  }

  if (imageUrl && !imageFailed) {
    return (
      <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-background">
        <img
          key={imageUrl}
          src={imageUrl}
          alt={isFiat ? `${currency.name} flag` : `${currency.name} icon`}
          width={44}
          height={44}
          loading="lazy"
          decoding="async"
          onError={() => {
            console.error(
              `Failed to load image for ${currency.code}:`,
              imageUrl,
            );
            setImageFailed(true);
          }}
          className={
            isFiat
              ? "h-full w-full object-cover"
              : "h-full w-full object-contain p-1.5"
          }
        />
      </div>
    );
  }

  return (
    <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-muted text-xs font-bold text-foreground">
      {currency.symbol?.slice(0, 3).toUpperCase() ||
        currency.code.slice(0, 3).toUpperCase()}
    </div>
  );
}

function CurrencySelect({
  value,
  onChange,
  currencies,
  loading = false,
  label,
}: CurrencySelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const selectedCurrency = useMemo(
    () =>
      currencies.find(
        (currency) => currency.code.toUpperCase() === value.toUpperCase(),
      ),
    [currencies, value],
  );

  const filteredCurrencies = useMemo(() => {
    const search = query.trim().toLowerCase();

    return currencies
      .filter((currency) => currency.enabled)
      .filter((currency) => {
        if (!search) return true;

        return (
          currency.code.toLowerCase().includes(search) ||
          currency.name.toLowerCase().includes(search) ||
          currency.symbol?.toLowerCase().includes(search) ||
          currency.iso?.toLowerCase().includes(search)
        );
      })
      .sort(
        (a, b) =>
          (a.sort_order ?? Number.MAX_SAFE_INTEGER) -
          (b.sort_order ?? Number.MAX_SAFE_INTEGER),
      );
  }, [currencies, query]);

  const closeDropdown = useCallback(() => {
    setOpen(false);
    setQuery("");
  }, []);

  useEffect(() => {
    if (open) {
      const timer = window.setTimeout(() => searchRef.current?.focus(), 50);

      return () => window.clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeDropdown();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, closeDropdown]);

  return (
    <div className="relative min-w-0 w-full">
      <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </label>

      <button
        type="button"
        aria-label={`Select ${label.toLowerCase()} currency`}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={loading || currencies.length === 0}
        onClick={() => setOpen((previous) => !previous)}
        className="flex min-h-[76px] w-full items-center gap-3 rounded-xl border border-border bg-background px-3 py-3 text-left transition hover:border-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-60 sm:px-4"
      >
        <CurrencyFlag currency={selectedCurrency} />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-base font-bold text-foreground sm:text-lg">
              {selectedCurrency?.code ?? value}
            </span>

            {selectedCurrency && (
              <span className="rounded-md border border-border bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">
                {getCurrencyType(selectedCurrency)}
              </span>
            )}
          </div>

          <p className="mt-1 truncate text-xs text-muted-foreground sm:text-sm">
            {loading
              ? "Loading currencies..."
              : (selectedCurrency?.name ?? "Currency")}
          </p>
        </div>

        {loading ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
        ) : (
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        )}
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-label="Close currency selector"
            onClick={closeDropdown}
            className="fixed inset-0 z-40 cursor-default"
          />

          <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-2xl">
            <div className="border-b border-border p-3">
              <div className="flex items-center gap-2 rounded-lg border border-border bg-background px-3">
                <Search className="h-4 w-4 shrink-0 text-muted-foreground" />

                <input
                  ref={searchRef}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search name or symbol..."
                  aria-label="Search currencies"
                  className="h-10 min-w-0 w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
                />
              </div>
            </div>

            <div
              role="listbox"
              aria-label={`${label} currencies`}
              className="max-h-64 overflow-y-auto p-2"
            >
              {filteredCurrencies.length === 0 ? (
                <div className="px-3 py-8 text-center">
                  <Search className="mx-auto mb-2 h-5 w-5 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    No currencies found.
                  </p>
                </div>
              ) : (
                filteredCurrencies.map((currency) => {
                  const selected =
                    currency.code.toUpperCase() === value.toUpperCase();

                  return (
                    <button
                      key={currency.id ?? currency.code}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => {
                        onChange(currency.code.toUpperCase());
                        closeDropdown();
                      }}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left transition ${
                        selected ? "bg-primary/10" : "hover:bg-accent"
                      }`}
                    >
                      <CurrencyFlag currency={currency} />

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-foreground">
                            {currency.code.toUpperCase()}
                          </span>

                          <span className="rounded border border-border px-1.5 py-0.5 text-[9px] uppercase text-muted-foreground">
                            {getCurrencyType(currency)}
                          </span>
                        </div>

                        <p className="truncate text-xs text-muted-foreground">
                          {currency.name}
                        </p>
                      </div>

                      {selected && (
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function Converter({
  initialFrom,
  initialTo,
  initialAmount,
  compact = false,
}: ConverterProps) {
  const navigate = useNavigate();

  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [assetsLoading, setAssetsLoading] = useState(true);
  const [assetsError, setAssetsError] = useState<string | null>(null);

  const [from, setFrom] = useState(initialFrom?.trim().toUpperCase() || "NGN");
  const [to, setTo] = useState(initialTo?.trim().toUpperCase() || "USDT");
  const [amount, setAmount] = useState(String(initialAmount ?? "1000000"));

  const [quote, setQuote] = useState<ConverterQuote | null>(null);
  const [market, setMarket] = useState<ConverterMarket | null>(null);

  const [quoteLoading, setQuoteLoading] = useState(false);
  const [marketLoading, setMarketLoading] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [marketError, setMarketError] = useState<string | null>(null);

  const quoteRequestId = useRef(0);
  const marketRequestId = useRef(0);

  const formatCurrencyAmount = useCallback(
    (amount: string | number | bigint, code: string) => {
      const currency = currencies.find(
        (item) => item.code.toUpperCase() === code.toUpperCase(),
      );

      return formatEvmHumanBalance(amount, currency?.isFiat ?? false);
    },
    [currencies],
  );

  // Load supported currencies.
  useEffect(() => {
    let cancelled = false;

    const loadCurrencies = async () => {
      setAssetsLoading(true);
      setAssetsError(null);

      try {
        const response = await makeRequest.get("/converter/assets");
        const data = response.data?.data ?? response.data;

        if (!Array.isArray(data)) {
          throw new Error("The server returned an invalid currency list.");
        }

        if (!cancelled) {
          setCurrencies(data as Currency[]);
        }
      } catch {
        if (!cancelled) {
          setAssetsError(
            "Unable to load currencies. Please refresh and try again.",
          );
        }
      } finally {
        if (!cancelled) {
          setAssetsLoading(false);
        }
      }
    };

    void loadCurrencies();

    return () => {
      cancelled = true;
    };
  }, []);

  // Keep initial values synchronized when the parent changes them.
  useEffect(() => {
    if (initialFrom?.trim()) {
      setFrom(initialFrom.trim().toUpperCase());
    }
  }, [initialFrom]);

  useEffect(() => {
    if (initialTo?.trim()) {
      setTo(initialTo.trim().toUpperCase());
    }
  }, [initialTo]);

  useEffect(() => {
    if (initialAmount !== undefined) {
      setAmount(String(initialAmount));
    }
  }, [initialAmount]);

  // Ensure selected currencies exist in the available list.
  useEffect(() => {
    if (assetsLoading || currencies.length === 0) return;

    const enabledCurrencies = currencies.filter((currency) => currency.enabled);

    if (enabledCurrencies.length === 0) return;

    const hasFrom = enabledCurrencies.some(
      (currency) => currency.code.toUpperCase() === from,
    );

    const hasTo = enabledCurrencies.some(
      (currency) => currency.code.toUpperCase() === to,
    );

    if (!hasFrom) {
      setFrom(enabledCurrencies[0].code.toUpperCase());
    }

    if (!hasTo) {
      const alternative = enabledCurrencies.find(
        (currency) => currency.code.toUpperCase() !== from,
      );

      if (alternative) {
        setTo(alternative.code.toUpperCase());
      }
    }
  }, [assetsLoading, currencies, from, to]);

  const sameCurrency = from.toUpperCase() === to.toUpperCase();

  const amountIsValid = useMemo(() => {
    const trimmed = amount.trim();

    if (!trimmed || !/^\d+(\.\d+)?$/.test(trimmed)) {
      return false;
    }

    const numericAmount = Number(trimmed);

    return Number.isFinite(numericAmount) && numericAmount > 0;
  }, [amount]);

  const fromCurrency = useMemo(
    () => currencies.find((currency) => currency.code.toUpperCase() === from),
    [currencies, from],
  );

  const toCurrency = useMemo(
    () => currencies.find((currency) => currency.code.toUpperCase() === to),
    [currencies, to],
  );

  // Load indicative conversion quote.
  const loadQuote = useCallback(async () => {
    const requestId = ++quoteRequestId.current;

    if (!from || !to || sameCurrency || !amountIsValid) {
      setQuote(null);
      setQuoteLoading(false);
      setError(null);
      return;
    }

    setQuoteLoading(true);
    setError(null);

    try {
      const response = await makeRequest.get("/converter/quote", {
        params: {
          from,
          to,
          amount: amount.trim(),
        },
      });

      if (requestId !== quoteRequestId.current) return;

      const data = response.data?.data ?? response.data;

      if (
        !data ||
        !Number.isFinite(Number(data.rate)) ||
        !Number.isFinite(Number(data.destinationAmount))
      ) {
        throw new Error("Invalid quote response.");
      }

      setQuote(data as ConverterQuote);
    } catch {
      if (requestId === quoteRequestId.current) {
        setQuote(null);
        setError("The current exchange rate is unavailable.");
      }
    } finally {
      if (requestId === quoteRequestId.current) {
        setQuoteLoading(false);
      }
    }
  }, [from, to, amount, amountIsValid, sameCurrency]);

  // Load merchant market information.
  const loadMarket = useCallback(async () => {
    const requestId = ++marketRequestId.current;

    if (!from || !to || sameCurrency || !amountIsValid) {
      setMarket(null);
      setMarketLoading(false);
      setMarketError(null);
      return;
    }

    setMarketLoading(true);
    setMarketError(null);

    try {
      const response = await makeRequest.get("/advertisement/market", {
        params: {
          from,
          to,
          amount: amount.trim(),
        },
      });

      if (requestId !== marketRequestId.current) return;

      const data = response.data?.data ?? response.data;

      setMarket(data as ConverterMarket);
    } catch {
      if (requestId === marketRequestId.current) {
        setMarket(null);
        setMarketError("Unable to check merchant offers right now.");
      }
    } finally {
      if (requestId === marketRequestId.current) {
        setMarketLoading(false);
      }
    }
  }, [from, to, amount, amountIsValid, sameCurrency]);

  // Debounce network requests while the user is typing.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadQuote();
    }, 350);

    return () => window.clearTimeout(timer);
  }, [loadQuote]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadMarket();
    }, 350);

    return () => window.clearTimeout(timer);
  }, [loadMarket]);

  const indicativeRate = quote ? Number(quote.rate) : 0;
  const result = quote ? Number(quote.destinationAmount) : 0;

  const merchantCount = market?.merchantCount ?? 0;
  const liquidity = Number(market?.liquidity ?? 0);
  const lowRate = Number(market?.lowRate ?? 0);
  const highRate = Number(market?.highRate ?? 0);

  const handleSwap = useCallback(() => {
    setFrom(to);
    setTo(from);

    setQuote(null);
    setMarket(null);
    setError(null);
    setMarketError(null);
  }, [from, to]);

  const handleFindOffers = useCallback(() => {
    if (!quote || quoteLoading || !amountIsValid || sameCurrency) {
      return;
    }

    const params = new URLSearchParams({
      from,
      to,
      amount: amount.trim(),
    });

    navigate(`/p2p?${params.toString()}`);
  }, [
    quote,
    quoteLoading,
    amountIsValid,
    sameCurrency,
    from,
    to,
    amount,
    navigate,
  ]);

  const handleRefresh = useCallback(() => {
    void loadQuote();
    void loadMarket();
  }, [loadQuote, loadMarket]);

  return (
    <section className="w-full min-w-0">
      <div
        className={`w-full min-w-0 max-w-none overflow-visible rounded-2xl border border-border bg-card text-card-foreground shadow-xl ${
          compact ? "p-4 sm:p-5" : "p-4 sm:p-6 lg:p-8"
        }`}
      >
        {/* Header */}
        <div className="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="mb-2 flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10">
                <ArrowDownUp className="h-5 w-5 text-primary" />
              </div>

              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
                BuyFiat Exchange
              </span>
            </div>

            <h2 className="text-xl font-bold tracking-tight text-foreground sm:text-2xl lg:text-3xl">
              Currency & Crypto Converter
            </h2>

            <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
              Convert between supported currencies and cryptocurrencies using
              indicative exchange rates.
            </p>
          </div>

          <button
            type="button"
            onClick={handleRefresh}
            disabled={
              assetsLoading ||
              quoteLoading ||
              marketLoading ||
              sameCurrency ||
              !amountIsValid
            }
            className="inline-flex shrink-0 items-center justify-center gap-2 self-start rounded-lg border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw
              className={`h-4 w-4 ${
                quoteLoading || marketLoading ? "animate-spin" : ""
              }`}
            />
            Refresh rates
          </button>
        </div>

        {/* Currency loading / error */}
        {assetsError && (
          <div
            role="alert"
            className="mb-5 flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive"
          >
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <div className="min-w-0 flex-1">{assetsError}</div>

            <button
              type="button"
              onClick={() => window.location.reload()}
              className="shrink-0 font-semibold underline underline-offset-4"
            >
              Retry
            </button>
          </div>
        )}

        {/* Converter fields */}
        <div className="grid w-full min-w-0 grid-cols-1 items-start gap-4 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:gap-5 xl:gap-8">
          {/* Source currency */}
          <div className="min-w-0 space-y-4">
            <CurrencySelect
              label="You send"
              value={from}
              onChange={(value) => {
                setFrom(value);
                setError(null);
              }}
              currencies={currencies}
              loading={assetsLoading}
            />

            <div className="rounded-xl border border-border bg-background p-4 sm:p-5">
              <label
                htmlFor="converter-amount"
                className="mb-3 block text-xs font-semibold uppercase tracking-wider text-muted-foreground"
              >
                Amount to convert
              </label>

              <div className="flex min-w-0 items-center gap-2">
                <span className="shrink-0 text-lg font-semibold text-primary sm:text-xl">
                  {fromCurrency?.symbol || from}
                </span>

                <input
                  id="converter-amount"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  spellCheck={false}
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  placeholder="Enter amount"
                  aria-invalid={amount.length > 0 && !amountIsValid}
                  className="min-w-0 w-full border-0 bg-transparent text-right font-mono text-2xl font-bold tracking-tight text-foreground outline-none placeholder:text-muted-foreground/50 focus:ring-0 sm:text-3xl lg:text-4xl"
                />
              </div>

              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
                <span className="text-xs text-muted-foreground">
                  {fromCurrency?.name ?? from}
                </span>

                <span className="text-xs text-muted-foreground">
                  Source amount
                </span>
              </div>
            </div>
          </div>

          {/* Swap control */}
          <div className="flex items-center justify-center md:pt-9">
            <button
              type="button"
              onClick={handleSwap}
              disabled={assetsLoading || currencies.length < 2}
              aria-label="Swap currencies"
              title="Swap currencies"
              className="group flex h-12 w-12 items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-primary transition duration-200 hover:rotate-180 hover:border-primary hover:bg-primary hover:text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50 md:h-11 md:w-11"
            >
              <ArrowDownUp className="h-5 w-5" />
            </button>
          </div>

          {/* Destination currency */}
          <div className="min-w-0 space-y-4">
            <CurrencySelect
              label="You receive"
              value={to}
              onChange={(value) => {
                setTo(value);
                setError(null);
              }}
              currencies={currencies}
              loading={assetsLoading}
            />

            <div className="rounded-xl border border-primary/30 bg-primary/[0.04] p-4 sm:p-5">
              <div className="mb-3 flex items-center justify-between gap-2">
                <label
                  htmlFor="converter-result"
                  className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                >
                  Estimated amount
                </label>

                {quote && !quoteLoading && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-500">
                    <CheckCircle2 className="h-3 w-3" />
                    Indicative
                  </span>
                )}
              </div>

              <div className="flex min-w-0 items-center gap-2">
                <span className="shrink-0 text-lg font-semibold text-primary sm:text-xl">
                  {toCurrency?.symbol || to}
                </span>

                <div
                  id="converter-result"
                  aria-live="polite"
                  className="flex min-h-10 min-w-0 w-full items-center justify-end overflow-hidden text-right font-mono text-2xl font-bold tracking-tight text-primary sm:text-3xl lg:text-4xl"
                >
                  {quoteLoading ? (
                    <Loader2 className="h-7 w-7 animate-spin text-primary" />
                  ) : quote ? (
                    <span className="break-all">
                      {formatCurrencyAmount(result, to)}
                    </span>
                  ) : (
                    <span className="text-muted-foreground/50">—</span>
                  )}
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
                <span className="text-xs text-muted-foreground">
                  {toCurrency?.name ?? to}
                </span>

                <span className="text-xs text-muted-foreground">
                  Destination amount
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Validation and quote messages */}
        <div className="mt-4 space-y-2">
          {sameCurrency && (
            <div
              role="status"
              className="flex items-start gap-2 rounded-lg border border-border bg-muted/50 p-3 text-sm text-muted-foreground"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              Select two different currencies to convert.
            </div>
          )}

          {!sameCurrency && amount.length > 0 && !amountIsValid && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              Enter a valid amount greater than zero.
            </div>
          )}

          {!sameCurrency && amountIsValid && error && !quoteLoading && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {error}
            </div>
          )}
        </div>

        {/* Exchange rate */}
        <div className="mt-6 rounded-xl border border-border bg-muted/30 p-4 sm:mt-8 sm:p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-border bg-card">
                <TrendingUp className="h-5 w-5 text-primary" />
              </div>

              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Indicative exchange rate
                </p>

                {quote && indicativeRate > 0 ? (
                  <p className="mt-1 break-words font-mono text-base font-bold text-foreground sm:text-lg">
                    1 {from} = {formatCurrencyAmount(1 / indicativeRate, to)}{" "}
                    {to}
                  </p>
                ) : (
                  <p className="mt-1 text-sm text-muted-foreground">
                    {quoteLoading
                      ? "Fetching the latest rate..."
                      : "Rate will appear when a quote is available."}
                  </p>
                )}
              </div>
            </div>

            <div className="shrink-0 sm:text-right">
              <p className="text-xs text-muted-foreground">Quote status</p>

              <p className="mt-1 text-sm font-semibold text-foreground">
                {quoteLoading
                  ? "Updating..."
                  : quote
                    ? "Rate available"
                    : "Awaiting quote"}
              </p>

              {quote?.generatedAt && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Updated {timeAgo(quote.generatedAt)}
                </p>
              )}
            </div>
          </div>

          <p className="mt-4 border-t border-border pt-3 text-xs leading-5 text-muted-foreground">
            Exchange rates are indicative and may change. The final amount may
            vary depending on merchant pricing, liquidity, fees, and execution
            conditions.
          </p>
        </div>

        {/* Merchant market */}
        <div className="mt-5 overflow-hidden rounded-xl border border-border">
          <div className="flex flex-col gap-3 border-b border-border bg-muted/30 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-border bg-card">
                <Store className="h-5 w-5 text-primary" />
              </div>

              <div>
                <h3 className="text-base font-bold text-foreground">
                  Merchant market
                </h3>

                <p className="mt-1 text-xs text-muted-foreground">
                  Check market pricing and available liquidity.
                </p>
              </div>
            </div>

            {market && !marketLoading && (
              <span
                className={`inline-flex w-fit items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ${
                  market.available
                    ? "bg-emerald-500/10 text-emerald-500"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${
                    market.available ? "bg-emerald-500" : "bg-muted-foreground"
                  }`}
                />
                {market.available ? "Market available" : "No active market"}
              </span>
            )}
          </div>

          <div className="p-4 sm:p-5">
            {marketLoading ? (
              <div className="flex min-h-32 flex-col items-center justify-center gap-3 text-center">
                <Loader2 className="h-7 w-7 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">
                  Checking merchant offers...
                </p>
              </div>
            ) : market ? (
              <>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="rounded-xl border border-border bg-background p-4">
                    <p className="text-xs font-medium text-muted-foreground">
                      Lowest market rate
                    </p>

                    <p className="mt-2 break-words font-mono text-lg font-bold text-foreground sm:text-xl">
                      {lowRate > 0
                        ? formatCurrencyAmount(
                            lowRate,
                            market.rateCurrency || to,
                          )
                        : "—"}
                    </p>

                    <p className="mt-1 text-xs text-muted-foreground">
                      Per {market.rateCurrency || to}
                    </p>
                  </div>

                  <div className="rounded-xl border border-border bg-background p-4">
                    <p className="text-xs font-medium text-muted-foreground">
                      Highest market rate
                    </p>

                    <p className="mt-2 break-words font-mono text-lg font-bold text-foreground sm:text-xl">
                      {highRate > 0
                        ? formatCurrencyAmount(
                            highRate,
                            market.rateCurrency || to,
                          )
                        : "—"}
                    </p>

                    <p className="mt-1 text-xs text-muted-foreground">
                      Per {market.rateCurrency || to}
                    </p>
                  </div>

                  <div className="rounded-xl border border-border bg-background p-4">
                    <p className="text-xs font-medium text-muted-foreground">
                      Active merchants
                    </p>

                    <p className="mt-2 font-mono text-2xl font-bold text-foreground">
                      {merchantCount.toLocaleString()}
                    </p>

                    <p className="mt-1 text-xs text-muted-foreground">
                      Available providers
                    </p>
                  </div>
                </div>

                <div className="mt-3 flex flex-col gap-2 rounded-xl border border-border bg-muted/30 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">
                      Reported market liquidity
                    </p>

                    <p className="mt-1 font-mono text-lg font-bold text-foreground">
                      {Number.isFinite(liquidity)
                        ? formatCurrencyAmount(
                            liquidity,
                            market.liquidityCurrency || to,
                          )
                        : "—"}
                      <span className="text-sm text-muted-foreground">
                        {market.liquidityCurrency || to}
                      </span>
                    </p>
                  </div>

                  <p className="max-w-md text-xs leading-5 text-muted-foreground sm:text-right">
                    Market prices and liquidity are provided by the merchant
                    market and may differ from the indicative conversion quote.
                  </p>
                </div>

                {market.message && (
                  <p className="mt-3 text-sm text-muted-foreground">
                    {market.message}
                  </p>
                )}
              </>
            ) : (
              <div className="flex min-h-28 flex-col items-center justify-center gap-2 text-center">
                {marketError ? (
                  <>
                    <AlertCircle className="h-6 w-6 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      {marketError}
                    </p>
                  </>
                ) : (
                  <>
                    <Store className="h-6 w-6 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      {sameCurrency
                        ? "Choose different currencies to check the market."
                        : amountIsValid
                          ? "Market information will appear when available."
                          : "Enter a valid amount to check merchant offers."}
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Marketplace action */}
        <div className="mt-6 flex flex-col gap-3 sm:mt-8 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-xl text-xs leading-5 text-muted-foreground">
            Compare available offers before making a transaction. The displayed
            conversion is an estimate, not a guaranteed execution price.
          </p>

          <button
            type="button"
            onClick={handleFindOffers}
            disabled={!quote || quoteLoading || !amountIsValid || sameCurrency}
            className="inline-flex min-h-12 w-full shrink-0 items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-bold text-primary-foreground shadow-sm transition hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
          >
            Find Available Offers
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
